// src/components/intake/QuestionsPage.jsx
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Button } from "../ui";
import Loader from "../Loader";
import { getIntake } from "../../shared/_services/intake.services";
import { LOAD_FAILED, TRY_AGAIN } from "../onboarding/onboardingCopy";
import PlaidRow from "./PlaidRow";
import QuestionItem from "./QuestionItem";
import {
  ANSWERS_LOAD_FAILED,
  QP_H1,
  QP_INTRO,
  QP_STAFF_TITLE,
} from "./intakeCopy";

// Reads GET /onboarding/intake (a staff reader passes the client) and keeps
// the shown answers, which a save rewrites.
function useIntakeAnswers(clientId) {
  const [load, setLoad] = useState({ status: "loading", data: null });
  const read = useCallback(async () => {
    setLoad({ status: "loading", data: null });
    try {
      const res = await getIntake(clientId);
      setLoad({ status: "ready", data: res.data });
    } catch (_) {
      setLoad({ status: "error", data: null });
    }
  }, [clientId]);
  useEffect(() => {
    read();
  }, [read]);
  const saved = (pageKey, answer) =>
    setLoad((l) => ({
      ...l,
      data: { ...l.data, answers: { ...l.data.answers, [pageKey]: answer } },
    }));
  // The Plaid row's link or consent changes the page's `plaid`, which the
  // items read to offer figures (R394).
  const setPlaid = (plaid) =>
    setLoad((l) => ({ ...l, data: { ...l.data, plaid } }));
  return { load, read, saved, setPlaid };
}

function Failed({ onRetry, message = LOAD_FAILED }) {
  return (
    <div role="alert" className="tw-mt-6">
      <span>{message}</span>{" "}
      <Button variant="text" onClick={onRetry}>
        {TRY_AGAIN}
      </Button>
    </div>
  );
}

// /questionnaire (spec § Questions page): one item per served page, in list
// order, each editable in place; the Plaid row sits before the first amounts
// page (between Q4 and Debts today). `?edit=<pageKey>` (every dashboard edit
// link, decision 27) opens that item in edit mode; `&part=` focuses a Q3 part.
// No Start over, no Re-take, no Full setup.
export default function QuestionsPage() {
  const [params] = useSearchParams();
  const editKey = params.get("edit");
  const part = params.get("part") || undefined;
  const { load, read, saved, setPlaid } = useIntakeAnswers();
  const data = load.data;
  const firstAmounts = data?.pages.find((p) => p.kind === "amounts");
  // SP6: Profile's "See the figures" arrives at #qp-q5_debts-h; once the
  // items have drawn, that item scrolls into view (once per arrival).
  const { hash } = useLocation();
  const scrolledTo = useRef(null);
  useEffect(() => {
    if (!data || !hash || scrolledTo.current === hash) return;
    scrolledTo.current = hash;
    document
      .getElementById(decodeURIComponent(hash.slice(1)))
      ?.scrollIntoView();
  }, [data, hash]);

  return (
    <div className="stars-dark tw-min-h-screen tw-bg-bg tw-px-4 tw-pb-16 tw-pt-6 tw-font-sans tw-text-text lg:tw-px-8">
      <div className="tw-max-w-4xl">
        <h1 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-text">{QP_H1}</h1>
        <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-text-muted">
          {QP_INTRO}
        </p>
        {load.status === "loading" && <Loader />}
        {load.status === "error" && <Failed onRetry={read} />}
        {data && data.vault !== "ok" && (
          <Failed onRetry={read} message={ANSWERS_LOAD_FAILED} />
        )}
        {data && (
          <div className="tw-mt-6 tw-grid tw-gap-4">
            {data.pages.map((page) => (
              <Fragment key={page.key}>
                {page === firstAmounts && (
                  <PlaidRow plaid={data.plaid} onPlaid={setPlaid} />
                )}
                <QuestionItem
                  page={page}
                  answer={data.answers?.[page.key] ?? null}
                  vaultOk={data.vault === "ok"}
                  plaid={data.plaid}
                  onSaved={saved}
                  startEditing={page.key === editKey}
                  focusPart={page.key === editKey ? part : undefined}
                />
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Staff on a client's dashboard: the same items read-only, titled
// "Questionnaire answers" (decision 26), from GET /intake?clientId=.
export function StaffAnswers({ clientId }) {
  const { load, read } = useIntakeAnswers(clientId);
  const data = load.data;
  return (
    <section
      aria-labelledby="staff-answers-h"
      className="tw-mt-8 tw-font-sans tw-text-text"
    >
      <h2 id="staff-answers-h" className="tw-m-0 tw-text-xl tw-font-bold tw-text-text">
        {QP_STAFF_TITLE}
      </h2>
      {load.status === "loading" && <Loader />}
      {load.status === "error" && <Failed onRetry={read} />}
      {data && data.vault !== "ok" && (
        <Failed onRetry={read} message={ANSWERS_LOAD_FAILED} />
      )}
      {data && (
        <div className="tw-mt-4 tw-grid tw-gap-4">
          {data.pages.map((page) => (
            <QuestionItem
              key={page.key}
              page={page}
              answer={data.answers?.[page.key] ?? null}
              vaultOk={data.vault === "ok"}
              staff
              Heading="h3"
            />
          ))}
        </div>
      )}
    </section>
  );
}
